import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import _ from 'lodash';
import { GridCellParams } from '@mui/x-data-grid-pro';
import { Badge, Box, Link, Typography } from '@mui/material';

import { translate as t } from 'hooks/useTranslations';
import { QuickSearch } from 'components/_atoms';
import useAccreditationBody from '../../_data_sources/AccreditationBodies/useAccreditationBody';
import useAccreditationVersions from '../../_data_sources/AccreditationVersions/useAccreditationVersions';
import { AccreditationVersion } from '../../../_types/awb';
import useUrlParams from 'hooks/useUrlParams';
import { ServerErrorResponse } from 'data_sources/constants';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import PageToolbar from '_lex/_atoms/PageToolbar';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import { AppContext } from 'views/App/context';

type AccreditingBodyVersionsListParams = {
  name?: string | null;
};

const defaultParams: AccreditingBodyVersionsListParams = {
  name: null,
};

const AccreditationVersionsList: React.FC = () => {
  const { id: accreditationBodyId } = useParams();
  const [params, setParams] = useUrlParams(defaultParams);
  const [accreditationVersions, setAccreditationVersions] = useState<AccreditationVersion[]>([]);
  const [lastId, setLastId] = useState<string | null>(null); // Track the last item ID created or updated
  const { currentOrganizationId } = useContext(AppContext);
  const bodyQuery = useAccreditationBody(currentOrganizationId ?? '', accreditationBodyId ?? '');
  const updateParams = useCallback(
    (updatedParams: AccreditingBodyVersionsListParams) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const queryParams = useMemo(() => {
    const newParams = { ...params };

    return _.omitBy(newParams, _.isEmpty);
  }, [params]);

  const gridViewQuery = useAccreditationVersions<AccreditationVersion>(
    currentOrganizationId ?? '',
    accreditationBodyId ?? '',
    queryParams,
    (errors: ServerErrorResponse) => {
      console.error('Error fetching accreditation versions:', errors);
    }
  );

  useEffect(() => {
    if (gridViewQuery.data && !gridViewQuery.isError) {
      const fetchedAccreditationVersions = gridViewQuery.data.pages.flatMap((page) => page.content) || [];
      setAccreditationVersions(fetchedAccreditationVersions);
    }
  }, [gridViewQuery.data, gridViewQuery.isError]);

  const columns: ColumnDef = useMemo(
    () => ({
      abbreviation: {
        header: t('awb.accreditingBodyVersions.properties.abbreviation'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return (
            <Link
              id={`accrediting_body_${row.id}`}
              href={`./${row.id}/standards`}
            >
              {row.abbreviation}
            </Link>
          );
        },
      },
      version: {
        header: t('awb.accreditingBodyVersions.properties.version'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{row.version}</span>;
        },
      },
    }),
    [t]
  );
  useEffect(() => {
    if (lastId && accreditationVersions && document.getElementById(`accrediting_body_${lastId}`)) {
      setLastId(null); // Reset lastId after scrolling
      document.getElementById(`accrediting_body_${lastId}`)?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [lastId, accreditationVersions]);

  const gridViewQueryData = useMemo(
    () => ({
      hasNextPage: gridViewQuery.hasNextPage,
      isFetchingNextPage: gridViewQuery.isFetchingNextPage,
      fetchNextPage: gridViewQuery.fetchNextPage,
      isError: gridViewQuery.isError,
      isLoading: gridViewQuery.isLoading,
      error: gridViewQuery.error,
      data: {
        pages:
          gridViewQuery.data?.pages.map((page) => ({
            count: page.count,
            rows: page.content,
          })) || [],
      },
    }),
    [gridViewQuery]
  );
  return (
    <CommonLayout
      headerProps={{
        title: t('awb.accreditingBodyVersions.title', '', { bodyName: bodyQuery.data?.name || '' }),
        subtitle: bodyQuery.data?.abbreviation,
        titleBadge:
          (gridViewQuery.data?.pages[0]?.count ?? 0) > 0 ? (
            <Badge
              variant='inline'
              badgeContent={gridViewQuery.data?.pages[0]?.count}
              color='neutral'
              sx={{ textTransform: 'uppercase' }}
            />
          ) : undefined,
        breadcrumbItems: [
          <Link
            key='accreditation'
            href='/admin/accreditation'
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            {t('awb.accreditingBodies.title')}
          </Link>,
          <Typography
            key='versions'
            color='text.primary'
          >
            {bodyQuery.data?.name || ''}
          </Typography>,
        ],
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 4, minHeight: 0 }}>
        <PageToolbar
          leftItems={
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: 3,
                flex: 1,
              }}
            >
              <QuickSearch
                id='keyword_search'
                initialValue={params.name || ''}
                onChange={(val) => updateParams({ name: val })}
                placeholder={t('awb.accreditingBodyVersions.search.placeholder')}
              />
              <Box />
            </Box>
          }
        />
        <GridSection
          columns={columns}
          data={accreditationVersions}
          query={gridViewQueryData}
        />
      </Box>
    </CommonLayout>
  );
};

export default AccreditationVersionsList;
